// src/index.ts
import {
  http,
  isHex,
  bytesToString,
  toBytes
} from "viem";
import {
  connect,
  getConnectedAddress,
  initConfig,
  sendTransaction,
  signMessage,
  signTypedData,
  disconnect
} from "@joyid/evm";
import { getConnectedAddress as getConnectedAddress2, initConfig as initConfig2, disconnect as disconnect2 } from "@joyid/evm";
function convertHexToUtf8(value) {
  if (isHex(value)) {
    return bytesToString(toBytes(value));
  }
  return value;
}
var CHAIN_ID_STORAGE_KEY = "joyid:ethereum:chainId";
var safeExec = (fn) => {
  try {
    return fn();
  } catch (error) {
    return null;
  }
};
var EthereumProvider = class {
  constructor(chains, config) {
    this.account = null;
    this.request = async (args) => {
      switch (args.method) {
        case "eth_requestAccounts": {
          const account = await this.connect();
          this.account = account;
          return [account];
        }
        case "eth_accounts":
          return [this.account];
        case "eth_chainId":
          return this.chainId;
        case "eth_sign": {
          throw new Error("eth_sign is not supported, use personal_sign instead");
        }
        case "wallet_switchEthereumChain": {
          return void 0;
        }
        case "personal_sign": {
          const [data, address] = args.params;
          const msg = convertHexToUtf8(data);
          const sig = await signMessage(isHex(msg) ? toBytes(msg) : msg, address);
          return sig;
        }
        case "eth_sendTransaction": {
          const [tx] = args.params;
          const hash = await sendTransaction(tx);
          return hash;
        }
        case "eth_signTypedData_v4": {
          const [address, message] = args.params;
          const msg = convertHexToUtf8(message);
          const sig = await signTypedData(JSON.parse(msg), address);
          return sig;
        }
        default: {
          const httpProvider = http(this.rpcUrl);
          const chain = this.chains.find((c) => c.id === this.chainId);
          if (chain == null) {
            throw new Error(
              `Unsupported chain id: ${this.chainId}, chainId must be one of ${this.chains.map((c) => c.id).join(",")}`
            );
          }
          return httpProvider({
            chain
          }).request(args);
        }
      }
    };
    let chainId = config.network?.chainId || chains[0].id;
    const chainIdFromStorage = safeExec(
      () => localStorage.getItem(CHAIN_ID_STORAGE_KEY)
    );
    if (chainIdFromStorage != null) {
      chainId = Number(chainIdFromStorage);
    }
    this.chainId = chainId;
    const rpcURL = config.rpcURL || chains[0].rpcUrls.default?.http[0];
    this.rpcUrl = rpcURL;
    this.account = getConnectedAddress();
    this.chains = chains;
    this.initConfig(chainId, config);
  }
  getAccount() {
    return this.account || getConnectedAddress();
  }
  initConfig(chainId, config) {
    const chain = this.chains.find((c) => c.id === chainId);
    if (chain == null) {
      throw new Error(
        `Unsupported chain id: ${chainId}, chainId must be one of ${this.chains.map((c) => c.id).join(",")}`
      );
    }
    const rpcURL = chain.rpcUrls.default.http[0];
    if (!rpcURL) {
      throw new Error("No rpc url provided in chains config");
    }
    initConfig({
      ...config,
      rpcURL: chain.rpcUrls.default.http[0],
      network: {
        chainId: chain.id,
        name: chain.name
      }
    });
    this.rpcUrl = rpcURL;
    this.chainId = chainId;
    return chain;
  }
  switchChain(chainId) {
    const chain = this.initConfig(chainId);
    safeExec(() => {
      localStorage.setItem(CHAIN_ID_STORAGE_KEY, chainId.toString());
    });
    return chain;
  }
  getChainId() {
    return this.chainId;
  }
  async connect() {
    const account = await connect();
    this.account = account;
    return account;
  }
  // eslint-disable-next-line class-methods-use-this
  disconnect() {
    safeExec(() => {
      localStorage.removeItem(CHAIN_ID_STORAGE_KEY);
      disconnect();
    });
  }
};
export {
  EthereumProvider,
  disconnect2 as disconnect,
  getConnectedAddress2 as getConnectedAddress,
  initConfig2 as initConfig
};
